/**
 * TicketsList — table-style view of every order that has chat activity.
 *
 * Columns (admin): Order№ · Customer · Brand · Model · Engine · ECU · Created · LastUpdate · Msgs · Open
 * Columns (client):                    Order№ · Brand · Model · Engine · ECU · Created · LastUpdate · Msgs · Open
 *
 * Filters mirror the OrdersTable ones: order number, brand, model, engine,
 * ecu, vin, status, customer (admin only), date range, "unread only".
 *
 * Rows with unread messages are highlighted with a yellow tint and a red
 * badge showing the unread count. Clicking a row opens the OrderDetailDrawer
 * (which contains the chat) and marks it read on drawer close.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare, Loader2, Search, Filter, X, Package, Eye, Clock, Calendar,
} from 'lucide-react';
import OrderDetailDrawer from './OrderDetailDrawer';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import LegacyTicketDrawer from './LegacyTicketDrawer';
import Pagination from './Pagination';
import PageHeader from './ui-shared/PageHeader';
import FilterPanel from './ui-shared/FilterPanel';
import DateRangeFilter from './ui-shared/DateRangeFilter';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import SortableHeader, { useTableSort } from './SortableHeader';
import { subscribeUnreadRefresh } from '../lib/unreadBus';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const STATUS_OPTIONS = ['pending', 'open', 'working', 'waiting', 'completed', 'closed', 'cancelled'];

export default function TicketsList({ token, isAdmin }) {
  const { t } = useTranslation('tickets');
  const { t: tCommon } = useTranslation('common');
  const { t: tOrders } = useTranslation('orders');
  const tNav = (k) => tCommon(`nav.${k}`);
  // Admin UI stays English. tt = scope-aware translator.
  const tt = useCallback((key, fallback, ns) => {
    if (isAdmin) return fallback;
    const target = ns === 'orders' ? tOrders : ns === 'common' ? tCommon : t;
    return target(key, { defaultValue: fallback });
  }, [isAdmin, t, tOrders, tCommon]);
  const [items, setItems] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [legacyCount, setLegacyCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [openLegacyTicketId, setOpenLegacyTicketId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [filters, setFilters] = useState({
    order_number: '', manufacturer: '', model: '', engine: '', ecu_type: '', vin: '',
    status: '', customer_email: '', date_start: '', date_end: '',
    country: '', user_id: '', payment_company: '',
    unread_only: false, source: 'all',
  });
  // Admin-only filter options (countries + accounts + payment companies) —
  // pulled once from the shared /api/orders/filters/options endpoint so
  // Tickets stays in sync with the Orders table.
  const [adminOptions, setAdminOptions] = useState({ countries: [], accounts: [], payment_companies: [] });

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Debounce text-input filters so typing doesn't fire per-keystroke fetches
  const debouncedFilters = useDebouncedValue(filters, 400);
  // Server-side sort. Resets page to 1 on every sort change so the
  // operator always sees the top of the new ordering.
  const { sortBy, sortDir, onSortChange } = useTableSort({
    onPageReset: () => setPage(1),
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(debouncedFilters).forEach(([k, v]) => {
        if (v !== '' && v !== false && v != null) params.append(k, String(v));
      });
      // Server-driven pagination: ask only for the visible page. Tab
      // counters (current_total / legacy_total / total) come from the
      // server-side count_documents() so they stay accurate even when
      // the legacy collection has 17k+ conversations.
      params.set('skip', String((page - 1) * PAGE_SIZE));
      params.set('limit', String(PAGE_SIZE));
      // Server-side sort — applied AFTER filters + merge, BEFORE pagination.
      if (sortBy) {
        params.set('sort_by', sortBy);
        if (sortDir) params.set('sort_dir', sortDir);
      }
      const r = await fetch(`${API_URL}/api/orders/tickets-list?${params.toString()}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setItems(data.items || []);
      setUnreadTotal(data.unread_tickets ?? 0);
      setCurrentCount(data.current_total ?? data.current_count ?? 0);
      setLegacyCount(data.legacy_total ?? data.legacy_count ?? 0);
      setTotalCount(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, headers, page, sortBy, sortDir]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    const unsub = subscribeUnreadRefresh(load);
    return () => { clearInterval(id); unsub(); };
  }, [load]);
  // Reset to page 1 whenever the user changes filters — but NOT when the
  // page itself changes (that would create an infinite loop with `load`).
  useEffect(() => { setPage(1); }, [debouncedFilters]);

  // Load admin-only dropdown options once.
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/filters/options`, { headers });
        if (r.ok) {
          const d = await r.json();
          setAdminOptions({ countries: d.countries || [], accounts: d.accounts || [], payment_companies: d.payment_companies || [] });
        }
      } catch (_) { /* silent */ }
    })();
  }, [isAdmin, headers]);

  const resetFilters = () => setFilters({
    order_number: '', manufacturer: '', model: '', engine: '', ecu_type: '', vin: '',
    status: '', customer_email: '', date_start: '', date_end: '',
    country: '', user_id: '', payment_company: '', unread_only: false, source: 'all',
  });

  // The API already returns the visible page; no client-side slicing.
  // Total pages derive from the FULL filtered count, not the slice length,
  // so the user can paginate through every legacy conversation.
  const visibleTotal = filters.source === 'current' ? currentCount
                      : filters.source === 'legacy' ? legacyCount
                      : totalCount;
  const totalPages = Math.max(1, Math.ceil(visibleTotal / PAGE_SIZE));
  const pageItems = items;

  return (
    <div className="p-4 md:p-6 w-full lg:w-[70%] mx-auto" data-testid="tickets-page">
      <PageHeader
        icon={
          <div className="relative">
            <MessageSquare className="w-5 h-5" />
            {unreadTotal > 0 && (
              <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse border border-white" data-testid="tickets-unread-total">
                {unreadTotal}
              </span>
            )}
          </div>
        }
        title={tt('title', 'Tickets')}
        subtitle={
          unreadTotal > 0
            ? (unreadTotal === 1
                ? tt('subtitle_unread_one', `You have ${unreadTotal} ticket with new messages`, undefined).replace('{{count}}', unreadTotal)
                : tt('subtitle_unread_many', `You have ${unreadTotal} tickets with new messages`, undefined).replace('{{count}}', unreadTotal))
            : isAdmin ? 'All conversations across all orders' : tt('subtitle_default', 'Your conversations with operators')
        }
        testid="tickets-header"
        actions={
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.unread_only}
              onChange={(e) => setFilters({ ...filters, unread_only: e.target.checked })}
              className="accent-cyan-600"
              data-testid="tickets-unread-only"
            />
            {tt('filters.unread_only', 'Unread only')}
          </label>
        }
      />

      {/* Source filter chips (Cycle B): All / Current / Legacy.  */}
      <div className="flex flex-wrap gap-2 mb-3 text-xs items-center" data-testid="tickets-source-chips">
        <span className="text-slate-500 font-semibold">{tt('labels.type', 'Type', 'common')}:</span>
        {[
          ['all',     tt('source.all', 'All'),         totalCount],
          ['current', tt('source.current', 'Current'), currentCount],
          ['legacy',  tt('source.legacy', 'Legacy'),   legacyCount],
        ].map(([k, label, count]) => (
          <button
            key={k}
            onClick={() => setFilters({ ...filters, source: k })}
            className={`px-2.5 py-1 rounded-full border font-semibold ${
              filters.source === k
                ? 'bg-cyan-600 text-white border-cyan-600'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
            }`}
            data-testid={`tickets-source-chip-${k}`}
          >
            {label} ({count.toLocaleString()})
          </button>
        ))}
      </div>

      {/* Filters — wrapped in shared FilterPanel for visual consistency
          with Transactions/Orders. Basic: order/customer/vehicle/status/date.
          Advanced (admin only): country/account/payment company. */}
      {(() => {
        const FILTER_LABELS = {
          order_number: 'Order №', customer_email: 'Customer',
          manufacturer: 'Brand', model: 'Model', engine: 'Engine',
          ecu_type: 'ECU', vin: 'VIN', status: 'Status',
          date_start: 'Date from', date_end: 'Date to',
          country: 'Country', user_id: 'Account', payment_company: 'Seller co.',
          unread_only: 'Unread only',
        };
        const activeChips = Object.entries(filters)
          .filter(([k, v]) => v !== '' && v != null && v !== false && k !== 'source')
          .map(([k, v]) => ({
            key: k, label: FILTER_LABELS[k] || k,
            value: k === 'unread_only' ? 'yes' : String(v),
            onRemove: () => setFilters({ ...filters, [k]: k === 'unread_only' ? false : '' }),
          }));
        const advancedKeys = ['country', 'user_id', 'payment_company'];
        const advancedActiveCount = advancedKeys.filter((k) => filters[k]).length;
        return (
          <FilterPanel
            open={filtersOpen}
            onToggle={() => setFiltersOpen((s) => !s)}
            activeChips={activeChips}
            onClearAll={resetFilters}
            advancedOpen={advancedOpen}
            onToggleAdvanced={() => setAdvancedOpen((s) => !s)}
            advancedActiveCount={advancedActiveCount}
            testid="tickets-filters"
            labels={{
              filters: tt('panel.filters', 'Filters', 'orders'),
              hide: tt('panel.hide', 'Hide', 'orders'),
              show: tt('panel.show', 'Show', 'orders'),
              clear_all: tt('panel.clear_all', 'Clear all', 'orders'),
              apply: tt('panel.apply', 'Apply', 'orders'),
              clear: tt('panel.clear', 'Clear', 'orders'),
              advanced_filters: tt('panel.advanced_filters', 'Advanced filters', 'orders'),
              active: tt('panel.active', 'active', 'orders'),
              hide_filters: tt('panel.hide_filters', 'Hide filters', 'orders'),
              show_filters: tt('panel.show_filters', 'Show filters', 'orders'),
            }}
            advancedChildren={isAdmin ? (
              <>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Country</label>
                  <select value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} data-testid="tickets-filter-country" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">{tt('labels.all', 'All', 'common')}</option>
                    {adminOptions.countries.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Account</label>
                  <select value={filters.user_id} onChange={(e) => setFilters({ ...filters, user_id: e.target.value })} data-testid="tickets-filter-account" className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">{tt('labels.all', 'All', 'common')}</option>
                    {adminOptions.accounts.map((a) => (<option key={a.user_id} value={a.user_id}>{a.label}{a.email ? ` · ${a.email}` : ''}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Seller company</label>
                  <select value={filters.payment_company} onChange={(e) => setFilters({ ...filters, payment_company: e.target.value })} data-testid="tickets-filter-payment-company" title="Filter by the seller company that received PayPal payments." className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white">
                    <option value="">UK / EU / USA</option>
                    {adminOptions.payment_companies.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
              </>
            ) : null}
          >
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.order', 'Order №', 'orders')}</label>
              <input type="text" placeholder={tt('columns.order', 'Order number', 'orders')} value={filters.order_number} onChange={(e) => setFilters({ ...filters, order_number: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-order-number" />
            </div>
            {isAdmin && (
              <div>
                <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">Customer</label>
                <input type="text" placeholder="Name or email" value={filters.customer_email} onChange={(e) => setFilters({ ...filters, customer_email: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-customer" />
              </div>
            )}
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.manufacturer', 'Brand', 'orders')}</label>
              <input type="text" placeholder={tt('columns.manufacturer', 'Brand', 'orders')} value={filters.manufacturer} onChange={(e) => setFilters({ ...filters, manufacturer: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-brand" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.model', 'Model', 'orders')}</label>
              <input type="text" placeholder={tt('columns.model', 'Model', 'orders')} value={filters.model} onChange={(e) => setFilters({ ...filters, model: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-model" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.engine', 'Engine', 'orders')}</label>
              <input type="text" placeholder={tt('columns.engine', 'Engine', 'orders')} value={filters.engine} onChange={(e) => setFilters({ ...filters, engine: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-engine" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">ECU</label>
              <input type="text" placeholder={tt('columns.ecu_type', 'ECU type', 'orders')} value={filters.ecu_type} onChange={(e) => setFilters({ ...filters, ecu_type: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-ecu" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">VIN</label>
              <input type="text" placeholder="VIN" value={filters.vin} onChange={(e) => setFilters({ ...filters, vin: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-vin" />
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 mb-0.5 block uppercase tracking-wide">{tt('columns.status', 'Status', 'orders')}</label>
              <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="w-full h-8 px-1.5 text-[11px] rounded border border-slate-200 bg-white" data-testid="tickets-filter-status">
                <option value="">{tt('filters.all', 'Any status')}</option>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{tt(`status.${s}`, s, 'orders')}</option>)}
              </select>
            </div>
            <DateRangeFilter
              from={filters.date_start}
              to={filters.date_end}
              onChange={({ from, to }) => setFilters({ ...filters, date_start: from, date_end: to })}
              testidPrefix="tickets-filter-date"
              label={tt('date_label', 'Date', 'orders')}
              presetLabels={{
                today: tt('presets.today', 'Today', 'orders'),
                d7: tt('presets.d7', '7 days', 'orders'),
                d30: tt('presets.d30', '30 days', 'orders'),
                this_month: tt('presets.this_month', 'This month', 'orders'),
              }}
            />
          </FilterPanel>
        );
      })()}

      {/* Table — desktop only */}
      <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading && items.length === 0 ? (
          <p className="text-center py-12 text-slate-500"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />{tt('loading_tickets', 'Loading tickets…')}</p>
        ) : items.length === 0 ? (
          <p className="text-center py-12 text-slate-500">{tt('empty_state.no_filter_results', 'No tickets match these filters.')}</p>
        ) : (
        <div className="overflow-x-auto max-h-[calc(100vh-340px)]">
          <table className="ar-zebra w-full text-sm" data-testid="tickets-table">
            <thead className="sticky top-0 z-10 shadow-[0_2px_0_0_rgba(15,23,42,0.06)]">
              <tr className="bg-slate-200 text-slate-700 text-[11px] font-bold uppercase tracking-wider">
                <SortableHeader label={tt('columns.order', 'Order №', 'orders')} field="order_number" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                {isAdmin && <SortableHeader label="Customer" field="customer" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />}
                <SortableHeader label={tt('columns.manufacturer', 'Brand', 'orders')} field="brand" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.model', 'Model', 'orders')} field="model" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.engine', 'Engine', 'orders')} field="engine" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.ecu_type', 'ECU type', 'orders')} field="ecu_type" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns_extra.date_created', 'Date created')} field="date_created" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.updated', 'Last update')} field="last_update" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} className="hover:bg-slate-300/50" />
                <SortableHeader label={tt('columns.messages', 'Msgs')} field="msgs" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} align="center" className="hover:bg-slate-300/50" />
                <th className="px-3 py-3 text-right w-24">{tt('actions.open', 'Open')}</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((t) => {
                const hasUnread = t.unread_count > 0;
                const isLegacy = t.source === 'legacy';
                const rowKey = isLegacy ? `lg-${t.legacy_ticket_id}` : t.order_id;
                const openTicket = () => {
                  // Cycle B refinement: legacy tickets linked to a real
                  // order open the standard OrderDetailDrawer (same UX
                  // as new orders). Only orphan tickets — those whose
                  // legacy id_order has no matching row in imported
                  // orders — fall back to the standalone read-only
                  // LegacyTicketDrawer.
                  if (isLegacy && t.order_id) {
                    setOpenOrderId(t.order_id);
                  } else if (isLegacy) {
                    setOpenLegacyTicketId(t.legacy_ticket_id);
                  } else {
                    setOpenOrderId(t.order_id);
                  }
                };
                return (
                  <tr key={rowKey}
                      onClick={openTicket}
                      data-testid={`ticket-row-${rowKey}`}
                      className={`border-t border-slate-100 cursor-pointer transition-colors ${hasUnread ? 'bg-amber-50/70 hover:bg-amber-100' : isLegacy ? 'hover:bg-violet-50/40' : 'hover:bg-cyan-50/40'}`}>
                    <td className="px-2.5 py-2 font-mono font-bold text-cyan-700 whitespace-nowrap">
                      {hasUnread && <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse mr-1.5 align-middle" />}
                      {t.order_number}
                      {isLegacy && (
                        <span className="ml-1.5 inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-300 uppercase">
                          Legacy
                        </span>
                      )}
                      {t.is_orphan && (
                        <span className="ml-1 inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 uppercase">
                          Orphan
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-2.5 py-2 max-w-[160px]">
                        <button
                          onClick={(e) => { e.stopPropagation(); t.user_id && setOpenCustomerId(t.user_id); }}
                          data-testid={`tickets-customer-${t.order_id}`}
                          title={t.user_email || ''}
                          className="text-cyan-700 hover:text-cyan-900 hover:underline text-sm block truncate w-full text-left"
                        >
                          {[t.customer_first_name, t.customer_surname].filter(Boolean).join(' ') || t.user_email || '—'}
                        </button>
                      </td>
                    )}
                    <td className="px-2.5 py-2 text-slate-700 max-w-[120px] truncate" title={t.manufacturer || ''}>{t.manufacturer || '—'}</td>
                    <td className="px-2.5 py-2 text-slate-700 max-w-[140px] truncate" title={t.model || ''}>{t.model || '—'}</td>
                    <td className="px-2.5 py-2 text-slate-700 max-w-[140px] truncate" title={t.engine || ''}>{t.engine || '—'}</td>
                    <td className="px-2.5 py-2 text-slate-700 whitespace-nowrap max-w-[180px] truncate" title={t.ecu_type || ''}>{t.ecu_type || '—'}</td>
                    <td className="px-2.5 py-2 text-xs text-slate-500 whitespace-nowrap"><Calendar className="w-3 h-3 inline mr-1 opacity-60" />{fmt(t.created_at)}</td>
                    <td className={`px-2.5 py-2 text-xs whitespace-nowrap ${hasUnread ? 'font-bold text-amber-700' : 'text-slate-500'}`}>
                      <Clock className="w-3 h-3 inline mr-1 opacity-60" />{fmt(t.last_message_at)}
                    </td>
                    <td className="px-2.5 py-2 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${hasUnread ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                        <MessageSquare className="w-3 h-3" />
                        {t.message_count}
                        {hasUnread && <span className="text-[10px]">({t.unread_count})</span>}
                      </span>
                    </td>
                    <td className="px-2.5 py-2 text-right whitespace-nowrap">
                      <button onClick={(e) => { e.stopPropagation(); openTicket(); }}
                        data-testid={`ticket-open-${rowKey}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 rounded text-xs font-bold">
                        <Eye className="w-3.5 h-3.5" /> Open
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
        {items.length > 0 && (
          <Pagination page={page} total={visibleTotal} pageSize={PAGE_SIZE} onChange={setPage} label="tickets" isAdmin={isAdmin} />
        )}
      </div>

      {/* Mobile card stack */}
      <div className="md:hidden space-y-3" data-testid="tickets-mobile-cards">
        {loading && items.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 inline mr-2 animate-spin" /> Loading tickets…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">
            No tickets match these filters.
          </div>
        )}
        {pageItems.map((t) => {
          const hasUnread = t.unread_count > 0;
          const isLegacy = t.source === 'legacy';
          const rowKey = isLegacy ? `lg-${t.legacy_ticket_id}` : t.order_id;
          const openTicket = () => {
            if (isLegacy && t.order_id) setOpenOrderId(t.order_id);
            else if (isLegacy)          setOpenLegacyTicketId(t.legacy_ticket_id);
            else                        setOpenOrderId(t.order_id);
          };
          return (
            <button
              key={rowKey}
              type="button"
              onClick={openTicket}
              data-testid={`ticket-card-${rowKey}`}
              className={`block w-full text-left bg-white rounded-xl border ${hasUnread ? 'border-red-300 shadow-red-100' : 'border-slate-200'} shadow-sm overflow-hidden active:scale-[0.99] transition-transform`}
            >
              <div className={`px-4 py-3 flex items-center justify-between gap-2 border-b border-slate-200 ${hasUnread ? 'bg-gradient-to-r from-amber-50 to-rose-50' : 'bg-gradient-to-r from-slate-50 to-slate-100'}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase font-bold text-slate-500 tracking-wider flex items-center gap-1.5">
                    {hasUnread && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
                    Order №
                    {isLegacy && <span className="ml-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-300">Legacy</span>}
                    {t.is_orphan && <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Orphan</span>}
                  </p>
                  <p className="font-mono text-sm font-bold text-cyan-700 truncate">{t.order_number}</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0 ${hasUnread ? 'bg-red-500 text-white animate-pulse' : 'bg-slate-100 text-slate-600'}`}>
                  <MessageSquare className="w-3.5 h-3.5" />
                  {t.message_count}
                  {hasUnread && <span className="text-[10px] opacity-90">({t.unread_count})</span>}
                </span>
              </div>
              <dl className="px-4 py-2.5 text-sm divide-y divide-slate-100">
                {isAdmin && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Customer</dt>
                    <dd
                      onClick={(e) => { e.stopPropagation(); t.user_id && setOpenCustomerId(t.user_id); }}
                      data-testid={`tickets-customer-mob-${t.order_id}`}
                      className="text-cyan-700 hover:underline text-right break-all min-w-0"
                    >
                      {[t.customer_first_name, t.customer_surname].filter(Boolean).join(' ') || t.user_email || '—'}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Vehicle</dt>
                  <dd className="text-slate-800 text-right">
                    <span className="font-medium">{t.manufacturer || '—'}</span>
                    {t.model && <span className="text-slate-500"> · {t.model}</span>}
                  </dd>
                </div>
                {(t.engine || t.ecu_type) && (
                  <div className="flex justify-between py-1.5 gap-3 items-center">
                    <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Engine/ECU</dt>
                    <dd className="text-slate-700 text-right text-xs break-words">
                      {t.engine || '—'} {t.ecu_type ? `· ${t.ecu_type}` : ''}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Created</dt>
                  <dd className="text-slate-700 text-xs">{fmt(t.created_at)}</dd>
                </div>
                <div className="flex justify-between py-1.5 gap-3 items-center">
                  <dt className="text-slate-500 text-xs font-semibold uppercase tracking-wider shrink-0">Updated</dt>
                  <dd className={`text-xs ${hasUnread ? 'font-bold text-amber-700' : 'text-slate-700'}`}>
                    {fmt(t.last_message_at)}
                  </dd>
                </div>
              </dl>
              <div className={`px-4 py-2.5 border-t border-slate-200 flex items-center justify-center gap-1.5 text-sm font-bold ${hasUnread ? 'bg-red-500 text-white' : 'bg-cyan-50 text-cyan-700'}`}>
                <Eye className="w-4 h-4" /> Open chat
              </div>
            </button>
          );
        })}
        {items.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <Pagination page={page} total={visibleTotal} pageSize={PAGE_SIZE} onChange={setPage} label="tickets" isAdmin={isAdmin} />
          </div>
        )}
      </div>

      {openOrderId && (
        <OrderDetailDrawer
          order={{ id: openOrderId }}
          token={token}
          isAdmin={isAdmin}
          onClose={() => { setOpenOrderId(null); load(); }}
          onChanged={() => load()}
        />
      )}
      {openCustomerId && (
        <CustomerProfileDrawer
          token={token}
          customerId={openCustomerId}
          onClose={() => setOpenCustomerId(null)}
          onSaved={() => load()}
        />
      )}
      {openLegacyTicketId && (
        <LegacyTicketDrawer
          token={token}
          isAdmin={isAdmin}
          legacyTicketId={openLegacyTicketId}
          onClose={() => setOpenLegacyTicketId(null)}
        />
      )}
    </div>
  );
}
